import { useState, useRef, useCallback, useEffect } from 'react';
import { speak as ttsSpeak, cancelSpeech } from '../services/tts';
import { createRecognition } from '../services/speechRecognition';

const FATAL_ASR_ERRORS = ['not-allowed', 'service-not-allowed', 'language-not-supported'];
const MAX_FAILS_IN_ROW = 5;
const STALL_BACKOFF_MAX_MS = 20000;
const wc = (s) => s.split(/\s+/).filter(Boolean).length;

export const useSpeechCapture = ({ onResult, onSilenceTimeout, onFatal, silenceTimeout = 20000, stallTimeoutMs = 0, rotateMs = 0 }) => {
  const [status, setStatus] = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [isAudioBlocked, setIsAudioBlocked] = useState(false);

  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const sessionIdRef = useRef(0);
  const sessionTextRef = useRef('');    // финалы всех предыдущих экземпляров текущей сессии
  const sessionFinalRef = useRef('');   // финалы текущего экземпляра — переносятся в onend
  const isMountedRef = useRef(true);
  const audioRef = useRef(null);
  const pendingAudioRef = useRef(null);
  const failStreakRef = useRef(0);
  const restartTimerRef = useRef(null);
  const lastErrorRef = useRef(null);
  const lastInterimRef = useRef('');          // хвост интерима последнего onresult текущего экземпляра
  const lastResultAtRef = useRef(0);          // когда последний раз пришёл onresult
  const instanceStartedAtRef = useRef(0);
  const sessionHadResultRef = useRef(false);  // был ли хоть один результат в этой сессии (карточке)
  const stallStrikesRef = useRef(0);          // подряд сработавших stall-рестартов без новых результатов
  const watchdogRef = useRef(null);
  const rotateTimerRef = useRef(null);
  const stopGuardRef = useRef(null);
  const stallTimeoutMsRef = useRef(stallTimeoutMs);
  const rotateMsRef = useRef(rotateMs);

  const onResultRef = useRef(onResult);
  const onSilenceRef = useRef(onSilenceTimeout);
  const onFatalRef = useRef(onFatal);
  const silenceTimeoutRef = useRef(silenceTimeout);

  useEffect(() => {
    onResultRef.current = onResult;
    onSilenceRef.current = onSilenceTimeout;
    onFatalRef.current = onFatal;
    silenceTimeoutRef.current = silenceTimeout;
    stallTimeoutMsRef.current = stallTimeoutMs;
    rotateMsRef.current = rotateMs;
  }, [onResult, onSilenceTimeout, onFatal, silenceTimeout, stallTimeoutMs, rotateMs]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

    const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearInstanceTimers = useCallback(() => {
    clearInterval(watchdogRef.current); watchdogRef.current = null;
    clearTimeout(rotateTimerRef.current); rotateTimerRef.current = null;
    clearTimeout(stopGuardRef.current); stopGuardRef.current = null;
  }, []);

  const stopRecognition = useCallback(() => {
    sessionIdRef.current += 1;
    sessionTextRef.current = '';
    sessionFinalRef.current = '';
    lastInterimRef.current = '';
    clearTimeout(restartTimerRef.current);
    clearInstanceTimers();
    const rec = recognitionRef.current;
    recognitionRef.current = null;

    if (rec) {
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.abort();
      } catch (e) {}
    }
    clearSilenceTimer();
  }, [clearSilenceTimer, clearInstanceTimers]);

  const armSilenceTimer = useCallback((id) => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      if (id === sessionIdRef.current && isMountedRef.current) {
        if (onSilenceRef.current) onSilenceRef.current();
      }
    }, silenceTimeoutRef.current);
  }, [clearSilenceTimer]);

  const spawnInstance = useCallback((id) => {
    if (id !== sessionIdRef.current || !isMountedRef.current) return;
    const recognition = createRecognition();
    if (!recognition) { if (onFatalRef.current) onFatalRef.current('unsupported'); return; }

    const stale = () => id !== sessionIdRef.current || recognitionRef.current !== recognition || !isMountedRef.current;
    const sec = () => ((Date.now() - instanceStartedAtRef.current) / 1000).toFixed(1);
    let rotating = false;

    const old = recognitionRef.current;
    if (old) {
      old.onresult = null; old.onerror = null; old.onend = null;
      try { old.abort(); } catch (e) {}
    }
    clearInstanceTimers();
    recognitionRef.current = recognition;
    lastErrorRef.current = null;
    lastInterimRef.current = '';
    lastResultAtRef.current = Date.now();
    instanceStartedAtRef.current = Date.now();

    // забираем всё, что успел получить экземпляр: финалы + недофинализированный хвост интерима
    const carryOver = () => {
      sessionTextRef.current = [sessionTextRef.current, sessionFinalRef.current, lastInterimRef.current].filter(Boolean).join(' ');
      sessionFinalRef.current = '';
      lastInterimRef.current = '';
    };

    // единый путь пересоздания: onend, stall-сторож, плановая ротация
    const retire = (reason, delay) => {
      if (stale()) return;
      console.log(`[SPEECH] respawn (${reason}), экземпляр жил ${sec()}с`);
      carryOver();
      clearInstanceTimers();
      recognition.onresult = null; recognition.onerror = null; recognition.onend = null;
      try { recognition.abort(); } catch (e) {}
      recognitionRef.current = null;
      restartTimerRef.current = setTimeout(() => spawnInstance(id), delay);
    };

    // диагностика: события аудио-фронтенда Chrome (после retire stale() их глушит)
    ['start', 'audiostart', 'soundstart', 'speechstart', 'speechend', 'soundend', 'audioend', 'nomatch'].forEach((name) =>
      recognition.addEventListener(name, () => { if (!stale()) console.log(`[ASR] ${name} @${sec()}с`); })
    );

    recognition.onresult = (event) => {
      if (stale()) return;
      failStreakRef.current = 0;
      stallStrikesRef.current = 0;
      sessionHadResultRef.current = true;
      lastResultAtRef.current = Date.now();

      let interimText = '';
let newFinalText = '';
for (let i = 0; i < event.results.length; i++) {
  if (event.results[i].isFinal) newFinalText += event.results[i][0].transcript + ' ';
}
// Android Chrome: каждая правка interim приходит отдельным нефинальным
// результатом с полным текстом — берём только последний, иначе склеивается
const lastIdx = event.results.length - 1;
if (lastIdx >= 0 && !event.results[lastIdx].isFinal) {
  interimText = event.results[lastIdx][0].transcript;
}
      console.log(`[ASR] @${sec()}с n=${event.results.length} финал=${wc(newFinalText)} интерим=${wc(interimText)}`);

      lastInterimRef.current = interimText.trim();
      if (newFinalText.trim()) sessionFinalRef.current = newFinalText.trim();

      if (newFinalText.trim() || interimText.trim()) armSilenceTimer(id);

      setTranscript((sessionTextRef.current + ' ' + newFinalText + ' ' + interimText).trim());
      if (newFinalText.trim()) {
        setFinalTranscript((sessionTextRef.current + ' ' + newFinalText).trim());
      }

      if ((newFinalText.trim() || interimText.trim()) && onResultRef.current) {
        onResultRef.current(newFinalText.toLowerCase(), interimText.toLowerCase());
      }
    };

    recognition.onerror = (event) => {
      if (stale()) return;
      lastErrorRef.current = event.error;
      console.log(`[SPEECH] onerror: ${event.error} @${sec()}с`);
      if (FATAL_ASR_ERRORS.includes(event.error) && onFatalRef.current) onFatalRef.current(event.error);
    };

    recognition.onend = () => {
      if (stale()) return;
      console.log(`[SPEECH] onend @${sec()}с${rotating ? ' (ротация)' : ''}`);
      if (FATAL_ASR_ERRORS.includes(lastErrorRef.current)) { carryOver(); return; }
      if (rotating) failStreakRef.current = 0; else failStreakRef.current += 1;
      if (failStreakRef.current > MAX_FAILS_IN_ROW) {
        carryOver();
        if (onFatalRef.current) onFatalRef.current('too_many_restarts');
        return;
      }
      const delay = rotating ? 0 : Math.min(100 * 2 ** (failStreakRef.current - 1), 2000);
      retire('onend', delay);
    };

    // Сторож: после первых результатов N секунд тишины в потоке → новый экземпляр.
    // Если сразу после рестарта опять тихо, интервал удваивается (до 20 с), чтобы не
    // дёргать микрофон, пока пациент просто молчит.
    if (stallTimeoutMsRef.current > 0) {
      watchdogRef.current = setInterval(() => {
        if (stale()) { clearInterval(watchdogRef.current); return; }
        if (!sessionHadResultRef.current) return;       // на старте карточки тишина — не поломка
        const limit = Math.min(stallTimeoutMsRef.current * 2 ** stallStrikesRef.current, STALL_BACKOFF_MAX_MS);
        if (Date.now() - lastResultAtRef.current > limit) {
          stallStrikesRef.current += 1;
          retire(`stall: ${Math.round(limit / 1000)}с без результатов`, 50);
        }
      }, 500);
    }

    // Плановая ротация: stop() возвращает финал по уже захваченному звуку
    // (abort() его бы выбросил). Если onend не пришёл за 1.5 с — забираем сами.
    if (rotateMsRef.current > 0) {
      rotateTimerRef.current = setTimeout(() => {
        if (stale()) return;
        rotating = true;
        console.log(`[SPEECH] плановая ротация @${sec()}с`);
        try { recognition.stop(); } catch (e) {}
        stopGuardRef.current = setTimeout(() => retire('rotate-timeout', 0), 1500);
      }, rotateMsRef.current);
    }

    try {
      recognition.start();
    } catch (e) {
      failStreakRef.current += 1;
      if (failStreakRef.current > MAX_FAILS_IN_ROW) {
        if (onFatalRef.current) onFatalRef.current('start_failed');
        return;
      }
      restartTimerRef.current = setTimeout(() => spawnInstance(id), 300);
    }
  }, [armSilenceTimer, clearInstanceTimers]);

  const startListening = useCallback(() => {
    if (!isMountedRef.current) return;
    sessionIdRef.current += 1;
    const id = sessionIdRef.current;

    sessionTextRef.current = '';   // новая карточка — новая лента
    sessionFinalRef.current = '';
    failStreakRef.current = 0;
    sessionHadResultRef.current = false;   // новая карточка: результатов ещё не было
    stallStrikesRef.current = 0;
    lastInterimRef.current = '';
    clearTimeout(restartTimerRef.current);

    setStatus('listening');
    setTranscript('');
    setFinalTranscript('');

    armSilenceTimer(id);
    spawnInstance(id);
  }, [armSilenceTimer, spawnInstance]);

  const speak = useCallback((text, onEndCallback = null, onStartCallback = null) => {
    if (!text) {
      if (onStartCallback) onStartCallback();
      if (onEndCallback) onEndCallback();
      return;
    }

    cancelSpeech();
    stopRecognition();
    setStatus('speaking');

    ttsSpeak(text, {
      onStart: () => {
        if (!isMountedRef.current) return;
        if (onStartCallback) onStartCallback();
      },
      onEnd: () => {
        if (!isMountedRef.current) return;
        setStatus('idle');
        if (onEndCallback) {
          onEndCallback();
        } else {
          const id = sessionIdRef.current;
          setTimeout(() => {
            if (id === sessionIdRef.current && isMountedRef.current) startListening();
          }, 300);
        }
      }
    });
  }, [stopRecognition, startListening]);

  const playAudio = useCallback((url, onEndCallback = null, onStartCallback = null) => {
    cancelSpeech();
    stopRecognition();
    setStatus('speaking');
    setIsAudioBlocked(false);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const audio = new Audio(url);
    audioRef.current = audio;
    pendingAudioRef.current = { url, onEndCallback, onStartCallback };

    audio.onplay = () => {
      if (!isMountedRef.current) return;
      if (onStartCallback) onStartCallback();
    };

    audio.onended = () => {
      if (!isMountedRef.current) return;
      setStatus('idle');
      setIsAudioBlocked(false);
      if (onEndCallback) {
        onEndCallback();
      } else {
        const id = sessionIdRef.current;
        setTimeout(() => {
          if (id === sessionIdRef.current && isMountedRef.current) startListening();
        }, 300);
      }
    };

    audio.onerror = () => {
      if (!isMountedRef.current) return;
      console.error('[AUDIO ERROR]', 'URL:', url);
      setStatus('idle');
      setIsAudioBlocked(false);
      if (onEndCallback) onEndCallback();
      else startListening();
    };

    audio.play().catch(err => {
      if (err.name === 'NotAllowedError') {
        console.warn('[AUDIO BLOCKED] Браузер заблокировал автоплей. Нужно нажать кнопку.');
        pendingAudioRef.current = { url, onEndCallback, onStartCallback };
        setIsAudioBlocked(true);
      } else if (err.name !== 'AbortError') {
        console.error('[AUDIO ERROR]', err.name, 'URL:', url);
        setIsAudioBlocked(false);
        if (onEndCallback) onEndCallback();
        else startListening();
      } else {
        console.warn('[AUDIO ABORTED] Воспроизведение прервано (возможно, из-за Strict Mode).', 'URL:', url);
      }
    });
  }, [stopRecognition, startListening]);

  const resumeAudio = useCallback(() => {
    if (pendingAudioRef.current && audioRef.current) {
      setIsAudioBlocked(false);
      audioRef.current.play().catch(err => {
        console.error('Resume error:', err);
        if (pendingAudioRef.current.onEndCallback) pendingAudioRef.current.onEndCallback();
        else startListening();
      });
    }
  }, [startListening]);

  const stopAll = useCallback(() => {
    stopRecognition();
    cancelSpeech();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setStatus('idle');
  }, [stopRecognition]);

  useEffect(() => {
    return () => {
      stopRecognition();
      cancelSpeech();
      if (audioRef.current) audioRef.current.pause();
    };
  }, [stopRecognition]);

  return { status, transcript, finalTranscript, speak, playAudio, isAudioBlocked, resumeAudio, startListening, stopAll };
};